/**
 * Chart Editor Page
 * ==================
 * Main periodontal charting interface.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Save,
  CheckCircle,
  ArrowLeft,
  Users,
  Lock,
  FileDown,
  Mic,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { useChartStore, useIsChartEditable, useChartSummary } from '@/stores/chartStore';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ToothGrid } from '@/components/charting/ToothGrid';
import { SiteInput } from '@/components/charting/SiteInput';
import { ChartControls } from '@/components/charting/ChartControls';
import { VoiceInputBar } from '@/components/charting/VoiceInputBar';
import { VoiceRecordingPanel } from '@/components/charting/VoiceRecordingPanel';
import { ParsedVoiceData } from '@/hooks/useVoiceInput';
import { debounce, formatDateTime } from '@/lib/utils';
import { uploadVoiceInput } from '@/api/charts';
import FinalizedChartView from './FinalizedChartView';


// =============================================================================
// COMPONENT
// =============================================================================

export default function ChartEditor() {
  const { chartId } = useParams<{ chartId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const patientIdFromQuery = searchParams.get('patient');
  const isNewChart = chartId === 'new';

  const {
    chart,
    isLoading,
    isSaving,
    isDirty,
    error,
    selectedTooth,
    activeUsers,
    lastAutosaveAt,
    loadChart,
    createChart,
    triggerAutosave,
    saveChart, // Add saveChart
    finalizeChart,
    reset,
  } = useChartStore();

  const isEditable = useIsChartEditable();
  const summary = useChartSummary();
  const isFinalized = chart?.status === 'finalized';

  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [showVoiceRecording, setShowVoiceRecording] = useState(false);
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const [previousTooth, setPreviousTooth] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  
  // Add ref to prevent rapid double-toggles
  const lastToggleTimeRef = useRef(0);

  // Helper function to count teeth with actual data
  const getTeethWithDataCount = () => {
    if (!chart?.tooth_data) return 0;
    
    return chart.tooth_data.filter(tooth => 
      tooth.status !== 'present' || // Status changed from default
      tooth.mobility !== null || // Has mobility data
      tooth.furcation !== null || // Has furcation data
      (tooth.sites && tooth.sites.length > 0 && tooth.sites.some(site => 
        site.pocket_depth !== null || 
        site.gingival_margin !== null || 
        site.bleeding || 
        site.suppuration
      )) // Has actual site measurements
    ).length;
  };

  // Load chart on mount
  useEffect(() => {
    if (isNewChart && patientIdFromQuery) {
      createChart(patientIdFromQuery, searchParams.get('baseline') === 'true');
    } else if (chartId && !isNewChart) {
      loadChart(chartId);
    }

    return () => reset();
  }, [chartId, patientIdFromQuery, isNewChart]);

  // Autosave debounced
  const debouncedAutosave = useCallback(
    debounce(() => {
      if (isDirty && isEditable) {
        triggerAutosave();
      }
    }, 2000),
    [isDirty, isEditable]
  );

  useEffect(() => {
    if (isDirty) {
      debouncedAutosave();
    }
  }, [isDirty, debouncedAutosave]);

  // Save data when switching teeth
  useEffect(() => {
    if (selectedTooth !== previousTooth) {
      if (previousTooth !== null && isDirty) {
        // Save immediately when switching teeth
        triggerAutosave();
      }
      setPreviousTooth(selectedTooth);
    }
  }, [selectedTooth, previousTooth, isDirty, triggerAutosave]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        triggerAutosave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleManualSave = async () => {
    try {
      await saveChart();
      toast.success('Chart saved successfully');
    } catch (err) {
      toast.error('Failed to save chart');
      console.error('Save error:', err);
    }
  };

  const handleFinalize = async () => {
    try {
      const teethWithDataCount = getTeethWithDataCount();
      
      // Validate that essential data is present
      if (teethWithDataCount === 0) {
        toast.error('Please add tooth data before finalizing');
        return;
      }
      
      // Save any pending changes first
      if (isDirty) {
        toast.loading('Saving changes...');
        await saveChart();
      }
      
      // Then finalize the chart
      await finalizeChart({
        signature_confirmation: true,
        aap_stage: chart?.aap_stage ?? undefined,
        aap_grade: chart?.aap_grade ?? undefined,
        aap_extent: chart?.aap_extent ?? undefined,
      });
      
      toast.success('Chart finalized and saved successfully');
      setShowFinalizeModal(false);
      queryClient.invalidateQueries({ queryKey: ['charts'] });
      
      // Navigate to patient details page after a brief delay
      setTimeout(() => {
        if (chart?.patient_id) {
          navigate(`/patients/${chart.patient_id}`);
        } else {
          navigate('/charts');
        }
      }, 1500);
    } catch (err: any) {
      toast.error(err.message || 'Failed to finalize chart');
      console.error('Finalization error:', err);
    }
  };

  const handleExportPDF = async () => {
    if (!chart) {
      toast.error('No chart data available');
      return;
    }

    setIsExporting(true);
    const loadingToast = toast.loading('Generating PDF...');

    try {
      const token = useAuthStore.getState().accessToken;
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      console.log('Exporting PDF for chart:', chart.id);
      
      // Use authenticated PDF endpoint
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/charts/${chart.id}/export/pdf`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF export failed:', response.status, errorText);
        throw new Error(`Failed to export PDF: ${response.status}`);
      }

      console.log('PDF downloaded successfully');
      
      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `chart-${chart.id.substring(0, 8)}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF exported successfully', { id: loadingToast });
    } catch (err: any) {
      console.error('PDF export error:', err);
      toast.error(err.message || 'Failed to export PDF', { id: loadingToast });
    } finally {
      setIsExporting(false);
    }
  };



  const handleVoiceToggle = () => {
    const now = Date.now();
    const timeSinceLastToggle = now - lastToggleTimeRef.current;
    
    console.log('[ChartEditor] ========== handleVoiceToggle CALLED ==========');
    console.log('[ChartEditor] Time since last toggle:', timeSinceLastToggle, 'ms');
    console.log('[ChartEditor] Current voiceEnabled state:', voiceEnabled);
    
    // Prevent toggles within 500ms of each other (debounce)
    if (timeSinceLastToggle < 500) {
      console.log('[ChartEditor] BLOCKED - Ignoring rapid toggle - only', timeSinceLastToggle, 'ms since last toggle');
      return;
    }
    
    lastToggleTimeRef.current = now;
    console.log('[ChartEditor] Calling setVoiceEnabled - toggling from', voiceEnabled, 'to', !voiceEnabled);
    setVoiceEnabled(!voiceEnabled);
  };

  const handleVoiceRecordingSubmit = async (audioBlob: Blob, duration: number) => {
    if (!chart?.id) {
      toast.error('Chart not loaded');
      return;
    }

    setIsUploadingVoice(true);
    const loadingToast = toast.loading('Uploading voice recording...');

    try {
      const response = await uploadVoiceInput(chart.id, audioBlob);
      
      console.log('Voice upload response:', response);
      
      toast.success(
        `Recording uploaded successfully!`,
        { id: loadingToast }
      );

      // If there's extracted data, you could apply it to the chart here
      if (response.extracted_data) {
        console.log('Extracted data from AI:', response.extracted_data);
      }

      setShowVoiceRecording(false);
      
      // Reload the chart to get backend-applied changes
      try {
        console.log('Reloading chart:', chart.id);
        await loadChart(chart.id);
        toast.success('Chart updated with voice input data', { id: loadingToast });
      } catch (reloadErr) {
        console.error('Failed to reload chart:', reloadErr);
        toast.error('Recording saved, but failed to reload chart. Please refresh the page.', { id: loadingToast, duration: 5000 });
      }
    } catch (err: any) {
      console.error('Voice upload error:', err);
      toast.error(
        err.response?.data?.error?.message || 'Failed to upload recording',
        { id: loadingToast }
      );
    } finally {
      setIsUploadingVoice(false);
    }
  };

  const handleVoiceDataParsed = (data: ParsedVoiceData) => {
    console.log('Voice data parsed:', data);
    
    const { 
      updateSiteData,
      updateToothData, 
      selectTooth, 
      setClinicalNotes, 
      setAAPClassification,
      setRiskFactor,
      setReferral
    } = useChartStore.getState();
    
    switch (data.type) {
      case 'site_readings': {
        // Select the tooth automatically
        selectTooth(data.toothNumber);
        
        // Update each site with the data
        data.readings.forEach((reading) => {
          const siteData: any = {};
          
          if (reading.pocketDepth !== undefined) {
            siteData.pocket_depth = reading.pocketDepth;
          }
          
          if (reading.bleeding !== undefined) {
            siteData.bleeding = reading.bleeding;
          }
          
          updateSiteData(data.toothNumber, reading.site as any, siteData);
        });
        break;
      }
      
      case 'clinical_notes': {
        setClinicalNotes(data.notes);
        break;
      }
      
      case 'aap_classification': {
        // Get current values and update only what's provided
        const currentStage = chart?.aap_stage || '';
        const currentGrade = chart?.aap_grade || '';
        const currentExtent = chart?.aap_extent || '';
        
        setAAPClassification(
          data.stage || currentStage,
          data.grade || currentGrade,
          data.extent || currentExtent
        );
        break;
      }
      
      case 'risk_factor': {
        setRiskFactor(data.factor, data.enabled);
        break;
      }
      
      case 'referral': {
        setReferral(data.recommended, data.reason);
        break;
      }
      
      case 'tooth_status': {
        // Select the tooth and update its status
        selectTooth(data.toothNumber);
        updateToothData(data.toothNumber, { status: data.status });
        break;
      }
      
      case 'mobility': {
        // Select the tooth and update its mobility
        selectTooth(data.toothNumber);
        updateToothData(data.toothNumber, { mobility: data.mobility });
        break;
      }
      
      case 'furcation': {
        // Select the tooth and update its furcation
        selectTooth(data.toothNumber);
        updateToothData(data.toothNumber, { furcation: data.furcation });
        break;
      }
    }
    
    // Trigger autosave after a short delay
    setTimeout(() => {
      triggerAutosave();
    }, 500);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-center">
          <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto mb-4" />
          <p className="text-gray-500">Loading chart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  // If chart is finalized, show read-only view
  if (chart && isFinalized) {
    return <FinalizedChartView chart={chart} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-white border-b">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Periodontal Chart
            </h1>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              {chart && (
                <>
                  <span>Version {chart.version}</span>
                  <Badge className={chart.status === 'finalized' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {chart.status}
                  </Badge>
                  {chart.is_baseline && <Badge variant="secondary">Baseline</Badge>}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Active Users */}
          {activeUsers.length > 0 && (
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">
                {activeUsers.length} editing
              </span>
            </div>
          )}

          {/* Save Status */}
          {!isFinalized && (
            <div className="text-sm text-gray-500">
              {isSaving ? (
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                  Saving...
                </span>
              ) : lastAutosaveAt ? (
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full" />
                  Saved {formatDateTime(lastAutosaveAt.toISOString())}
                </span>
              ) : isDirty ? (
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-orange-400 rounded-full" />
                  Unsaved changes
                </span>
              ) : null}
            </div>
          )}

          {/* Voice Input Options - Only show if NOT finalized */}
          {!isFinalized && (
            <>
              <VoiceInputBar
                onDataParsed={handleVoiceDataParsed}
                isEnabled={voiceEnabled}
                onToggle={handleVoiceToggle}
              />
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowVoiceRecording(true)}
                disabled={voiceEnabled}
              >
                <Mic className="w-4 h-4 mr-2" />
                AI voice charting
              </Button>
            </>
          )}

          {/* Actions - Only show Save and Finalize if NOT finalized */}
          {!isFinalized && (
            <>
              <Button
                variant="outline"
                onClick={handleManualSave}
                disabled={!isDirty || isSaving}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button 
                onClick={() => setShowFinalizeModal(true)}
                disabled={isSaving}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Finalize
              </Button>
            </>
          )}

          {/* Finalized Badge and Export PDF - Show only for finalized charts */}
          {isFinalized && (
            <>
              <Badge variant="success" className="text-sm py-2 px-4">
                <CheckCircle className="w-4 h-4 mr-2" />
                Finalized
              </Badge>
              <Button 
                variant="outline"
                onClick={handleExportPDF}
                disabled={isExporting}
              >
                <FileDown className="w-4 h-4 mr-2" />
                {isExporting ? 'Generating...' : 'Export PDF'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Tooth Grid */}
        <div className="flex-1 overflow-auto p-6 bg-gray-50">
          <ToothGrid />
        </div>

        {/* Side Panel */}
        <div className="w-80 border-l bg-white overflow-auto">
          {selectedTooth ? (
            <SiteInput toothNumber={selectedTooth} />
          ) : (
            <div className="p-6">
              <ChartControls />
              
              {/* Summary Stats */}
              {summary && (
                <Card className="mt-6 p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Teeth Present:</span>
                      <span className="font-medium">{summary.teeth_present}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">BOP:</span>
                      <span className="font-medium">{summary.bop_percentage}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Avg PD:</span>
                      <span className="font-medium">{summary.avg_pocket_depth}mm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Sites ≥4mm:</span>
                      <span className="font-medium">{summary.pockets_4mm_plus}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Sites ≥6mm:</span>
                      <span className="font-medium text-red-600">{summary.pockets_6mm_plus}</span>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>



      {/* Voice Recording Modal */}
      <Modal
        isOpen={showVoiceRecording}
        onClose={() => !isUploadingVoice && setShowVoiceRecording(false)}
        title="AI Voice Charting"
      >
        <VoiceRecordingPanel
          onSubmit={handleVoiceRecordingSubmit}
          onCancel={() => setShowVoiceRecording(false)}
          isSubmitting={isUploadingVoice}
        />
      </Modal>

      {/* Finalize Modal */}
      <Modal
        isOpen={showFinalizeModal}
        onClose={() => setShowFinalizeModal(false)}
        title="Finalize Chart"
      >
        <div className="p-4">
          <p className="text-gray-600 mb-4">
            Finalizing will save all data and lock this chart, preventing further edits.
            Please confirm that:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 mb-4 space-y-1">
            <li>All tooth measurements have been recorded</li>
            <li>Clinical notes are complete</li>
            <li>AAP classification has been assigned (if applicable)</li>
            <li>All data has been reviewed for accuracy</li>
          </ul>
          
          {isDirty && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
              <p className="text-sm text-yellow-800">
                ⚠️ You have unsaved changes. They will be saved automatically before finalizing.
              </p>
            </div>
          )}
          
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
            <p className="text-sm text-blue-800 font-medium">
              📊 Summary: {getTeethWithDataCount()} {getTeethWithDataCount() === 1 ? 'tooth' : 'teeth'} recorded
            </p>
            {chart?.clinical_notes && chart.clinical_notes.length > 0 && (
              <p className="text-sm text-blue-800 mt-1">
                📝 Clinical notes: {chart.clinical_notes.length > 50 ? 'Complete' : 'Brief'}
              </p>
            )}
            {chart?.aap_stage && (
              <p className="text-sm text-blue-800 mt-1">
                🏥 AAP Classification: {chart.aap_stage}
                {chart.aap_grade && `, ${chart.aap_grade}`}
                {chart.aap_extent && `, ${chart.aap_extent}`}
              </p>
            )}
          </div>
          
          <div className="flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowFinalizeModal(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleFinalize} 
              isLoading={isSaving}
              disabled={isSaving}
            >
              <Lock className="w-4 h-4 mr-2" />
              {isSaving ? 'Finalizing...' : 'Finalize & Sign'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
